import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactAudioPlayer from 'react-audio-player';
import { SPINNER } from '../../../constants/components';
import LoadingPage from '../../../common/components/LoadingPage';
import GoButtonDark from '../../../common/components/GoButtonDark';
import AudioContainer from '../../../common/audio/audioContainer';
import ScreenErrorContainer from '../../../common/error/ScreenErrorContainer';
import IllustrationContainer from '../../../common/illustration/IllustrationContainer';
import '../../../stylesheets/lc.scss';
import VolumeIcon from '../../../images/audio.svg';
import VolumeIconWhite from '../../../images/audio-white.svg';
import ReplayIcon from '../../../images/replay.svg';

const STATUS = {
    IDLE: 'idle',
    PLAYING: 'playing',
    DONE: 'done',
};

const DEFAULT_REPLAYS = 0;
const MAX_REPLAYS = 1;
const DEFAULT_PROGRESS = 0;
const DEFAULT_MAX_PROGRESS = 100;
const LISTEN_INTERVAL = 1000;
const TIME_TICK = 1;

const LCReadingView = props => {
    const {
        showSpinner,
        fetchContent,
        isDataFetched,
        hideSpinner,
        isSpinnerVisible,
        onNext,
        goToQuestions,
        currentResult,
        createLCResult,
    } = props;

    useEffect(() => {
        if (isDataFetched) {
            hideSpinner();
        } else {
            showSpinner();
            fetchContent();
        }
    }, [isDataFetched]);

    useEffect(() => {
        if (!currentResult) createLCResult();
    }, []);

    const [status, setStatus] = useState(STATUS.IDLE);
    const [replays, setReplays] = useState(DEFAULT_REPLAYS);
    const [progress, setProgress] = useState(DEFAULT_PROGRESS);
    const [maxProgress, setMaxProgress] = useState(DEFAULT_MAX_PROGRESS);

    const onPlay = () => {
        setStatus(STATUS.PLAYING);
    };

    const onAudioLoad = audioData => {
        const { duration } = audioData.target;
        setMaxProgress(duration);
    };

    const onProgress = () => {
        setProgress(progress + TIME_TICK);
    };

    const onFinish = () => {
        setStatus(STATUS.DONE);
    };

    const onReplay = () => {
        if (replays < MAX_REPLAYS) {
            setProgress(DEFAULT_PROGRESS);
            setStatus(STATUS.PLAYING);
            setReplays(replays + 1);
        }
    };

    const onGoButtonClick = () => {
        goToQuestions();
    };

    return (
        <div>
            <ScreenErrorContainer onRetry={onNext} />
            <AudioContainer />
            {isDataFetched && (
                <div className="reading-container">
                    <IllustrationContainer
                        url={props.passage.image}
                        class="reading-image"
                        imageClass="reading-image"
                        altText=" "
                    />
                    {status === STATUS.IDLE && (
                        <button className="reading-button" aria-label="Play" onClick={onPlay}>
                            <img className="reading-icon" src={VolumeIcon} alt="Reading icon" />
                            <p className="reading-text">Play</p>
                        </button>
                    )}
                    {status === STATUS.PLAYING && (
                        <div className="reading-playback-container">
                            <ReactAudioPlayer
                                src={props.passage.audio}
                                onEnded={onFinish}
                                autoPlay
                                onListen={onProgress}
                                listenInterval={LISTEN_INTERVAL}
                                onLoadedMetadata={onAudioLoad}
                            />
                            <img
                                className="reading-icon-small"
                                src={VolumeIconWhite}
                                alt="Small reading icon"
                            />
                            <progress
                                className="reading-progress-bar"
                                value={progress}
                                max={maxProgress}
                            />
                        </div>
                    )}
                    {status === STATUS.DONE && replays < MAX_REPLAYS && (
                        <div className="reading-action-container">
                            <button
                                className="reading-button"
                                aria-label="Replay"
                                onClick={onReplay}
                            >
                                <img className="reading-icon" src={ReplayIcon} alt="Reading icon" />
                                <p className="reading-text">Replay</p>
                            </button>
                        </div>
                    )}
                    {status === STATUS.DONE && replays < MAX_REPLAYS && (
                        <GoButtonDark isEnabled={true} onClick={onGoButtonClick} text="Next" />
                    )}
                    {status === STATUS.DONE && replays >= MAX_REPLAYS && (
                        <GoButtonDark
                            className="go-button-no-replay"
                            isEnabled={true}
                            onClick={onGoButtonClick}
                            text="Next"
                        />
                    )}
                </div>
            )}
            {isSpinnerVisible && <LoadingPage spinnerConfig={SPINNER.DARK} />}
        </div>
    );
};

LCReadingView.propTypes = {
    showSpinner: PropTypes.func,
    fetchContent: PropTypes.func,
    hideSpinner: PropTypes.func,
    onNext: PropTypes.func,
    isDataFetched: PropTypes.bool,
    isSpinnerVisible: PropTypes.bool,
    isNextButtonActive: PropTypes.bool,
    passage: PropTypes.shape({ image: PropTypes.string, audio: PropTypes.string }),
    isScreenBlocked: PropTypes.bool,
    isScreenGreyedOut: PropTypes.bool,
    goToQuestions: PropTypes.func,
    currentResult: PropTypes.object,
    createLCResult: PropTypes.func,
};

LCReadingView.defaultProps = {
    showSpinner: () => {},
    fetchContent: () => {},
    hideSpinner: () => {},
    onNext: () => {},
    isDataFetched: false,
    isSpinnerVisible: false,
    isNextButtonActive: false,
    passage: { image: '', audio: '' },
    isScreenBlocked: false,
    isScreenGreyedOut: false,
    goToQuestions: () => {},
    currentResult: null,
    createLCResult: () => {},
};

export default LCReadingView;
